import { RefObject, useEffect, useRef } from "react";
import { usePageStore } from "../../usePageStore";
import styles from "./more-filters-component.module.css";
import {
  OfficeCategoryEnum,
  PurposeEnum,
  Search,
  TypeEnum,
} from "@/types/types";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { getCurrency } from "@/lib/currency-data/currency";
import { MdErrorOutline } from "react-icons/md";
import { useLocale, useTranslations } from "next-intl";
import Orientation from "./components/orientation";
import View from "./components/view";
import Facilities from "./components/facilities";
import { useListingsCacheStore } from "@/global-cache/useListingsCacheStore";
import { formatNumber, parseNumber } from "@/lib/number-field-formatter";
import CategorySelectorClient from "../page-searchbar/category/category-selector.client";
import BuildingTypeSelectorClient from "../page-searchbar/building-type/building-type-selector.client";
import OfficeTypeSelectorClient from "../page-searchbar/office-type/office-type-selector.client";
import { DeviceType } from "@/hooks/use-device-type";

//fix filters to represent the user preferences!!!!!!!
export default function MoreFiltersComponent({
  onClose,
  parentRef,
}: {
  onClose: () => void;
  parentRef:
    | RefObject<HTMLButtonElement | null>
    | RefObject<HTMLDivElement | null>;
}) {
  const filters = usePageStore().search;
  const searchFiltersStatus = usePageStore().searchFiltersStatus;
  const updateSearch = usePageStore().updateSearch;
  const resetSearchFilters = usePageStore().resetSearchFilters;
  const type = usePageStore((state) => state.search.type);
  const deviceType = usePageStore((state) => state.deviceType);
  const category = usePageStore((state) => state.search.category);
  const is_luxury = usePageStore((state) => state.search.is_luxury);
  const new_development = usePageStore((state) => state.search.new_development);
  // const refetchCardsList = usePageStore().refetchCardsList;
  const validateSeachFilters = usePageStore().validateSeachFilters;
  const refetchState = usePageStore().refetchState;
  const setCache = useListingsCacheStore((state) => state.setMultipleListings);
  // const fetchMapPoints = usePageStore().fetchMapPoints;
  const { userData } = useUserAuthDataContext();
  const userCurrency = getCurrency(userData.currency);
  const locale = useLocale();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const t = useTranslations("MoreFilters");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        !parentRef.current?.contains(event.target as Node)
      ) {
        const isNotValid = validateSeachFilters();
        if (isNotValid) {
          return;
        }
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, parentRef, validateSeachFilters]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (validateSeachFilters()) return;
        e.preventDefault();
        onClose();
        parentRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, parentRef, validateSeachFilters]);

  return (
    <div className={styles.advancedPanel} ref={wrapperRef}>
      <div className={styles.panelHeader}>
        <h3>{t("moreFilters")}</h3>
      </div>
      <div className={styles.filters}>
        {deviceType === DeviceType.mobile && (
          <>
            {type !== TypeEnum.APARTMENT_HOUSE && (
              <>
                <div className={styles.selectWrapper}>
                  <CategorySelectorClient
                    t={t}
                    type={type}
                    activeCategory={category}
                    updateSearch={updateSearch}
                    refetchState={async () => {
                      await refetchState(locale, setCache, true);
                    }}
                    fullWidth={true}
                    isMobileStyle={true}
                  />
                </div>
              </>
            )}
            {type === TypeEnum.APARTMENT && (
              <>
                <div className={styles.selectWrapper}>
                  <BuildingTypeSelectorClient
                    t={t}
                    updateSearch={updateSearch}
                    refetchState={async () => {
                      await refetchState(locale, setCache, true);
                    }}
                    fullWidth={true}
                    isMobileStyle={true}
                  />
                </div>
              </>
            )}
            {type === TypeEnum.OFFICE &&
              category === OfficeCategoryEnum.OFFICE && (
                <>
                  <div className={styles.selectWrapper}>
                    <OfficeTypeSelectorClient
                      t={t}
                      updateSearch={updateSearch}
                      refetchState={async () => {
                        await refetchState(locale, setCache, true);
                      }}
                      fullWidth={true}
                      isMobileStyle={true}
                    />
                  </div>
                </>
              )}
            {type !== TypeEnum.LAND && (
              <>
                <div
                  className={`${styles.selectWrapper} ${styles.isLuxury} ${
                    is_luxury && styles.isSelected
                  } ${styles.mobile}`}
                >
                  <button
                    onClick={async (e) => {
                      e.preventDefault();
                      updateSearch("is_luxury", !is_luxury);
                      await refetchState(locale, setCache, true);
                    }}
                    className={`${styles.pill} ${styles.togglePill} ${
                      is_luxury && styles.pillActive
                    }`}
                  >
                    <span>{t("isLuxury")}</span>
                  </button>
                </div>
              </>
            )}
            {type !== TypeEnum.LAND && (
              <>
                <div
                  className={`${styles.selectWrapper} ${
                    styles.newDevelopment
                  } ${new_development && styles.isSelected} ${styles.mobile}`}
                >
                  <button
                    onClick={async (e) => {
                      e.preventDefault();
                      updateSearch("new_development", !new_development);
                      await refetchState(locale, setCache, true);
                    }}
                    className={`${styles.pill} ${styles.togglePill} ${
                      new_development && styles.pillActive
                    }`}
                  >
                    <span>{t("isNewDevelopment")}</span>
                  </button>
                </div>
              </>
            )}
          </>
        )}
        {filters.type !== TypeEnum.LAND && filters.type !== TypeEnum.OFFICE && (
          <div className={styles.filterGroup}>
            <fieldset className={`${styles.fieldSet} ${styles.withMargin}`}>
              <legend className={styles.legend}>{t("numOfRoom")}</legend>
              <div className={styles.segmented}>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("rooms", {
                      number: 0,
                      is_fixed: filters.rooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.rooms.number === 0 && styles.segmentActive
                  }`}
                >
                  {t("any")}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("rooms", {
                      number: 1,
                      is_fixed: filters.rooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.rooms.number === 1 && styles.segmentActive
                  }`}
                >
                  {filters.rooms.is_fixed ? "1" : "1+"}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("rooms", {
                      number: 2,
                      is_fixed: filters.rooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.rooms.number === 2 && styles.segmentActive
                  }`}
                >
                  {filters.rooms.is_fixed ? "2" : "2+"}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("rooms", {
                      number: 3,
                      is_fixed: filters.rooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.rooms.number === 3 && styles.segmentActive
                  }`}
                >
                  {filters.rooms.is_fixed ? "3" : "3+"}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("rooms", {
                      number: 4,
                      is_fixed: filters.rooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.rooms.number === 4 && styles.segmentActive
                  }`}
                >
                  {filters.rooms.is_fixed ? "4" : "4+"}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("rooms", {
                      number: 5,
                      is_fixed: filters.rooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.rooms.number === 5 && styles.segmentActive
                  }`}
                >
                  {filters.rooms.is_fixed ? "5" : "5+"}
                </button>
              </div>
            </fieldset>

            <fieldset className={styles.fieldSet}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={filters.rooms.is_fixed}
                  name="exact-match-rooms"
                  onChange={async (e) => {
                    const isChecked = e.target.checked;
                    updateSearch("rooms", {
                      number: filters.rooms.number,
                      is_fixed: isChecked,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                />
                {t("exactNumOfRoom")}
              </label>
            </fieldset>
          </div>
        )}
        {filters.type !== TypeEnum.LAND && filters.type !== TypeEnum.OFFICE && (
          <div className={styles.filterGroup}>
            <fieldset className={`${styles.fieldSet} ${styles.withMargin}`}>
              <legend className={styles.legend}>{t("numOfBedroom")}</legend>
              <div className={styles.segmented}>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("bedrooms", {
                      number: 0,
                      is_fixed: filters.bedrooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.bedrooms.number === 0 && styles.segmentActive
                  }`}
                >
                  {t("any")}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("bedrooms", {
                      number: 1,
                      is_fixed: filters.bedrooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.bedrooms.number === 1 && styles.segmentActive
                  }`}
                >
                  {filters.bedrooms.is_fixed ? "1" : "1+"}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("bedrooms", {
                      number: 2,
                      is_fixed: filters.bedrooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.bedrooms.number === 2 && styles.segmentActive
                  }`}
                >
                  {filters.bedrooms.is_fixed ? "2" : "2+"}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("bedrooms", {
                      number: 3,
                      is_fixed: filters.bedrooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.bedrooms.number === 3 && styles.segmentActive
                  }`}
                >
                  {filters.bedrooms.is_fixed ? "3" : "3+"}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("bedrooms", {
                      number: 4,
                      is_fixed: filters.bedrooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.bedrooms.number === 4 && styles.segmentActive
                  }`}
                >
                  {filters.bedrooms.is_fixed ? "4" : "4+"}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("bedrooms", {
                      number: 5,
                      is_fixed: filters.bedrooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.bedrooms.number === 5 && styles.segmentActive
                  }`}
                >
                  {filters.bedrooms.is_fixed ? "5" : "5+"}
                </button>
              </div>
            </fieldset>

            <fieldset className={styles.fieldSet}>
              <label className={styles.checkbox}>
                <input
                  checked={filters.bedrooms.is_fixed}
                  type="checkbox"
                  name="exact-match-rooms"
                  onChange={async (e) => {
                    const isChecked = e.target.checked;
                    updateSearch("bedrooms", {
                      number: filters.bedrooms.number,
                      is_fixed: isChecked,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                />
                {t("exactNumOfBedroom")}
              </label>
            </fieldset>
          </div>
        )}
        {filters.type !== TypeEnum.LAND && filters.type !== TypeEnum.OFFICE && (
          <div className={styles.filterGroup}>
            <fieldset className={`${styles.fieldSet} ${styles.withMargin}`}>
              <legend className={styles.legend}>{t("numOfBathroom")}</legend>
              <div className={styles.segmented}>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("bathrooms", {
                      number: 0,
                      is_fixed: filters.bathrooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.bathrooms.number === 0 && styles.segmentActive
                  }`}
                >
                  {t("any")}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("bathrooms", {
                      number: 1,
                      is_fixed: filters.bathrooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.bathrooms.number === 1 && styles.segmentActive
                  }`}
                >
                  {filters.bathrooms.is_fixed ? "1" : "1+"}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("bathrooms", {
                      number: 2,
                      is_fixed: filters.bathrooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.bathrooms.number === 2 && styles.segmentActive
                  }`}
                >
                  {filters.bathrooms.is_fixed ? "2" : "2+"}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("bathrooms", {
                      number: 3,
                      is_fixed: filters.bathrooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.bathrooms.number === 3 && styles.segmentActive
                  }`}
                >
                  {filters.bathrooms.is_fixed ? "3" : "3+"}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("bathrooms", {
                      number: 4,
                      is_fixed: filters.bathrooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.bathrooms.number === 4 && styles.segmentActive
                  }`}
                >
                  {filters.bathrooms.is_fixed ? "4" : "4+"}
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    updateSearch("bathrooms", {
                      number: 5,
                      is_fixed: filters.bathrooms.is_fixed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                  className={`${styles.segment} ${
                    filters.bathrooms.number === 5 && styles.segmentActive
                  }`}
                >
                  {filters.bathrooms.is_fixed ? "5" : "5+"}
                </button>
              </div>
            </fieldset>

            <fieldset className={styles.fieldSet}>
              <label className={styles.checkbox}>
                <input
                  checked={filters.bathrooms.is_fixed}
                  type="checkbox"
                  name="exact-match-rooms"
                  onChange={async (e) => {
                    const isChecked = e.target.checked;
                    updateSearch("bathrooms", {
                      number: filters.bathrooms.number,
                      is_fixed: isChecked,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                />
                {t("exactNumOfBathroom")}
              </label>
            </fieldset>
          </div>
        )}
        <div className={styles.filterGroup}>
          <fieldset className={styles.fieldSet}>
            <legend className={styles.legend}>{t("priceRange")}</legend>
            {searchFiltersStatus.hasPriceError && (
              <span className={styles.errorMessage}>
                <span>
                  <MdErrorOutline />
                </span>
                {t("minimumError")}
              </span>
            )}
            <div className={styles.rangeInputs}>
              <div className={styles.inputWrapper}>
                <input
                  value={
                    filters.price.min !== 0
                      ? formatNumber(filters.price.min, locale || "de-DE")
                      : ""
                  }
                  className={styles.rangeInput}
                  min={0}
                  type="text"
                  inputMode="numeric"
                  placeholder="Min"
                  onKeyDown={(e) => {
                    if (
                      [
                        "Backspace",
                        "Delete",
                        "Tab",
                        "Escape",
                        "Enter",
                        "ArrowLeft",
                        "ArrowRight",
                      ].includes(e.key)
                    ) {
                      return;
                    }
                    if (!/^\d$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={async (e) => {
                    const raw = e.target.value;
                    const parsed = parseNumber(raw);
                    if (raw === "" || isNaN(parsed)) {
                      updateSearch("price", {
                        min: 0,
                        max: filters.price.max,
                      });
                      await refetchState(locale, setCache, true);
                      return;
                    }
                    updateSearch("price", {
                      min: parsed,
                      max: filters.price.max,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                />
                <span className={styles.currencySpan}>{userCurrency.Name}</span>
              </div>
              <span className={styles.separator}>-</span>
              <div className={styles.inputWrapper}>
                <input
                  value={
                    filters.price.max !== 0
                      ? formatNumber(filters.price.max, locale || "de-DE")
                      : ""
                  }
                  className={styles.rangeInput}
                  min={0}
                  type="text"
                  inputMode="numeric"
                  placeholder="Max"
                  onKeyDown={(e) => {
                    if (
                      [
                        "Backspace",
                        "Delete",
                        "Tab",
                        "Escape",
                        "Enter",
                        "ArrowLeft",
                        "ArrowRight",
                      ].includes(e.key)
                    ) {
                      return;
                    }
                    if (!/^\d$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={async (e) => {
                    const raw = e.target.value;
                    const parsed = parseNumber(raw);
                    if (raw === "" || isNaN(parsed)) {
                      updateSearch("price", {
                        min: filters.price.min,
                        max: 0,
                      });
                      await refetchState(locale, setCache, true);
                      return;
                    }
                    updateSearch("price", {
                      min: filters.price.min,
                      max: parsed,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                />
                <span className={styles.currencySpan}>{userCurrency.Name}</span>
              </div>
            </div>
          </fieldset>
        </div>
        {filters.type !== TypeEnum.LAND && (
          <div className={styles.filterGroup}>
            <fieldset className={styles.fieldSet}>
              <legend className={styles.legend}>{t("interiorSpace")}</legend>
              {searchFiltersStatus.hasInteriorSpaceError && (
                <span className={styles.errorMessage}>
                  <span>
                    <MdErrorOutline />
                  </span>
                  {t("minimumError")}
                </span>
              )}
              <div className={styles.rangeInputs}>
                <div className={styles.inputWrapper}>
                  <input
                    value={
                      filters.interior_space.min !== 0
                        ? formatNumber(
                            filters.interior_space.min,
                            locale || "de-DE"
                          )
                        : ""
                    }
                    className={styles.rangeInput}
                    min={0}
                    type="text"
                    inputMode="numeric"
                    placeholder="Min"
                    onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "Tab",
                          "Escape",
                          "Enter",
                          "ArrowLeft",
                          "ArrowRight",
                        ].includes(e.key)
                      ) {
                        return;
                      }
                      if (!/^\d$/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={async (e) => {
                      const raw = e.target.value;
                      const parsed = parseNumber(raw);
                      if (raw === "" || isNaN(parsed)) {
                        updateSearch("interior_space", {
                          min: 0,
                          max: filters.interior_space.max,
                        });
                        await refetchState(locale, setCache, true);
                        return;
                      }
                      updateSearch("interior_space", {
                        min: parsed,
                        max: filters.interior_space.max,
                      });
                      await refetchState(locale, setCache, true);
                    }}
                  />
                  <span className={styles.unitSystemSpan}>
                    {userData.unit_system === "Metric" ? "M" : "FT"}
                  </span>
                </div>
                <span className={styles.separator}>-</span>
                <div className={styles.inputWrapper}>
                  <input
                    value={
                      filters.interior_space.max !== 0
                        ? formatNumber(
                            filters.interior_space.max,
                            locale || "de-DE"
                          )
                        : ""
                    }
                    className={styles.rangeInput}
                    min={0}
                    type="text"
                    inputMode="numeric"
                    placeholder="Max"
                    onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "Tab",
                          "Escape",
                          "Enter",
                          "ArrowLeft",
                          "ArrowRight",
                        ].includes(e.key)
                      ) {
                        return;
                      }
                      if (!/^\d$/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={async (e) => {
                      const raw = e.target.value;
                      const parsed = parseNumber(raw);
                      if (raw === "" || isNaN(parsed)) {
                        updateSearch("interior_space", {
                          min: filters.interior_space.min,
                          max: 0,
                        });
                        await refetchState(locale, setCache, true);
                        return;
                      }
                      updateSearch("interior_space", {
                        min: filters.interior_space.min,
                        max: parsed,
                      });
                      await refetchState(locale, setCache, true);
                    }}
                  />
                  <span className={styles.unitSystemSpan}>
                    {userData.unit_system === "Metric" ? "M" : "FT"}
                  </span>
                </div>
              </div>
            </fieldset>
          </div>
        )}
        {filters.type !== TypeEnum.APARTMENT && (
          <div className={styles.filterGroup}>
            <fieldset className={styles.fieldSet}>
              <legend className={styles.legend}>{t("lotSize")}</legend>
              {searchFiltersStatus.hasLotSizeError && (
                <span className={styles.errorMessage}>
                  <span>
                    <MdErrorOutline />
                  </span>
                  {t("minimumError")}
                </span>
              )}
              <div className={styles.rangeInputs}>
                <div className={styles.inputWrapper}>
                  <input
                    value={
                      filters.lot_size.min !== 0
                        ? formatNumber(filters.lot_size.min, locale || "de-DE")
                        : ""
                    }
                    className={styles.rangeInput}
                    min={0}
                    type="text"
                    inputMode="numeric"
                    placeholder="Min"
                    onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "Tab",
                          "Escape",
                          "Enter",
                          "ArrowLeft",
                          "ArrowRight",
                        ].includes(e.key)
                      ) {
                        return;
                      }
                      if (!/^\d$/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={async (e) => {
                      const raw = e.target.value;
                      const parsed = parseNumber(raw);
                      if (raw === "" || isNaN(parsed)) {
                        updateSearch("lot_size", {
                          min: 0,
                          max: filters.lot_size.max,
                        });
                        await refetchState(locale, setCache, true);
                        return;
                      }
                      updateSearch("lot_size", {
                        min: parsed,
                        max: filters.lot_size.max,
                      });
                      await refetchState(locale, setCache, true);
                    }}
                  />
                  <span className={styles.unitSystemSpan}>
                    {userData.unit_system === "Metric" ? "M" : "FT"}
                  </span>
                </div>
                <span className={styles.separator}>-</span>
                <div className={styles.inputWrapper}>
                  <input
                    value={
                      filters.lot_size.max !== 0
                        ? formatNumber(filters.lot_size.max, locale || "de-DE")
                        : ""
                    }
                    className={styles.rangeInput}
                    min={0}
                    type="text"
                    inputMode="numeric"
                    placeholder="Max"
                    onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "Tab",
                          "Escape",
                          "Enter",
                          "ArrowLeft",
                          "ArrowRight",
                        ].includes(e.key)
                      ) {
                        return;
                      }
                      if (!/^\d$/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={async (e) => {
                      const raw = e.target.value;
                      const parsed = parseNumber(raw);
                      if (raw === "" || isNaN(parsed)) {
                        updateSearch("lot_size", {
                          min: filters.lot_size.min,
                          max: 0,
                        });
                        await refetchState(locale, setCache, true);
                        return;
                      }
                      updateSearch("lot_size", {
                        min: filters.lot_size.min,
                        max: parsed,
                      });
                      await refetchState(locale, setCache, true);
                    }}
                  />
                  <span className={styles.unitSystemSpan}>
                    {userData.unit_system === "Metric" ? "M" : "FT"}
                  </span>
                </div>
              </div>
            </fieldset>
          </div>
        )}
        {filters.type !== TypeEnum.LAND && (
          <div className={styles.filterGroup}>
            <fieldset className={styles.fieldSet}>
              <legend className={styles.legend}>{t("yearBuilt")}</legend>
              {searchFiltersStatus.hasYearBuiltError && (
                <span className={styles.errorMessage}>
                  <span>
                    <MdErrorOutline />
                  </span>
                  {t("minimumError")}
                </span>
              )}
              <div className={styles.rangeInputs}>
                <select
                  value={
                    filters.year_built.min !== 0 ? filters.year_built.min : ""
                  }
                  className={`${styles.input} ${styles.rangeSelect}`}
                  onChange={async (e) => {
                    const value = e.target.value;
                    updateSearch("year_built", {
                      min: value === "" ? 0 : parseInt(value),
                      max: filters.year_built.max,
                    });
                    await refetchState(locale, setCache, true);
                  }}
                >
                  <option value={0}>Min</option>
                  {(() => {
                    const currentYear = new Date().getFullYear();
                    const years: number[] = [];

                    // Add 6 future years (including current year)
                    for (let i = 5; i >= 0; i--) {
                      years.push(currentYear + i);
                    }

                    // Add 100 past years (excluding current year)
                    for (let i = 1; i <= 100; i++) {
                      years.push(currentYear - i);
                    }

                    return years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ));
                  })()}
                </select>
                <span className={styles.separator}>-</span>
                <select
                  value={
                    filters.year_built.max !== 0 ? filters.year_built.max : ""
                  }
                  className={`${styles.input} ${styles.yearBuilt}`}
                  onChange={async (e) => {
                    const value = e.target.value;
                    updateSearch("year_built", {
                      min: filters.year_built.min,
                      max: value === "" ? 0 : parseInt(value),
                    });
                    await refetchState(locale, setCache, true);
                  }}
                >
                  <option value={0}>Max</option>
                  {(() => {
                    const currentYear = new Date().getFullYear();
                    const years: number[] = [];

                    // Add 6 future years (including current year)
                    for (let i = 5; i >= 0; i--) {
                      years.push(currentYear + i);
                    }

                    // Add 100 past years (excluding current year)
                    for (let i = 1; i <= 100; i++) {
                      years.push(currentYear - i);
                    }

                    return years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ));
                  })()}
                </select>
              </div>
            </fieldset>
          </div>
        )}
        {filters.purpose === PurposeEnum.FORSALE && (
          <div className={styles.filterGroup}>
            <fieldset className={styles.fieldSet}>
              <legend className={styles.legend}>{t("buyersFee")}</legend>
              <div className={styles.rangeInputs}>
                <select
                  value={
                    filters.maxBuyersFee !== -1 ? filters.maxBuyersFee : ""
                  }
                  className={`${styles.input} ${styles.maxBuyersFee}`}
                  onChange={async (e) => {
                    const value = e.target.value;
                    updateSearch(
                      "maxBuyersFee",
                      value === "" ? -1 : parseInt(value)
                    );
                    await refetchState(locale, setCache, true);
                  }}
                >
                  <option value={-1}>{t("fee")} %</option>
                  {[...Array(31)].map((_, i) => (
                    <option key={i} value={i}>
                      {i}%
                    </option>
                  ))}
                </select>
              </div>
            </fieldset>
          </div>
        )}
        <Facilities
          t={t}
          updateSearch={async (key, value) => {
            updateSearch(key, value);
            await refetchState(locale, setCache, true);
          }}
          filters={filters}
        />
        <View
          t={t}
          updateSearch={async (key, value) => {
            updateSearch(key, value);
            await refetchState(locale, setCache, true);
          }}
          filters={filters}
        />
        {filters.type !== TypeEnum.LAND && (
          <Orientation
            t={t}
            updateSearch={async (key, value) => {
              updateSearch(key, value);
              await refetchState(locale, setCache, true);
            }}
            filters={filters}
          />
        )}
      </div>
      <div className={styles.panelFooter}>
        <div className={styles.footerWrapper}>
          <button
            onClick={async (e) => {
              e.preventDefault();
              resetSearchFilters();
              await refetchState(locale, setCache, true);
            }}
            className={styles.resetBtn}
          >
            {t("resetFilters")}
          </button>
          <button
            onClick={async (e) => {
              e.preventDefault();
              onClose();
            }}
            className={styles.applyBtn}
          >
            {t("apply")}
          </button>
        </div>
      </div>
    </div>
  );
}
